'use client';

import dynamic from 'next/dynamic';
import { FC } from 'react';

import { FlexibleSection } from '../flexibleContent.type';
import { CAR_QUALITY_ANIMATION_WRAPPER_ID } from './carQualityAnimation.constants';

interface Props extends FlexibleSection {
  sectionData: unknown;
}

const CarQualityAnimationSequence = dynamic(
  () => import('./sequence/CarQualityAnimationSequence').then((mod) => mod.CarQualityAnimationSequence),
  {
    ssr: false,
  },
);
const CarQualityAnimationWrapper = dynamic(
  () => import('./CarQualityAnimationWrapper').then((mod) => mod.CarQualityAnimationWrapper),
  {
    ssr: false,
  },
);
const CarQualityAnimationContentHero = dynamic(
  () => import('./content/CarQualityAnimationContentHero').then((mod) => mod.CarQualityAnimationContentHero),
  {
    ssr: false,
  },
);
const CarQualityAnimationContentLeftRight = dynamic(
  () => import('./content/CarQualityAnimationContentLeftRight').then((mod) => mod.CarQualityAnimationContentLeftRight),
  {
    ssr: false,
  },
);
const ScrollProgress = dynamic(() => import('./ScrollProgress/ScrollProgress').then((mod) => mod.ScrollProgress), {
  ssr: false,
});

const VIDEO_TRANSFORM = 'tr:w-412,h-232,ac-none';

const animationPoints = [
  [0, 0.005], // Hero
  [0.097, 0.165], // Handpicked cars
  [0.2, 0.285], // Test drivers
  [0.455, 0.52], // Inspection process
  [0.555, 0.635], // Reconditioning
  [0.885, 1], // Showroom standard
] as const;

const snapPoints: [number, number][] = animationPoints.map(([from, to]) => [from, to]);

export const CarQualityAnimation: FC<Props> = () => {
  return (
    <section className="header-transparent relative h-2500" id={CAR_QUALITY_ANIMATION_WRAPPER_ID}>
      <CarQualityAnimationWrapper>
        <CarQualityAnimationSequence />
        <CarQualityAnimationContentHero
          from={animationPoints[0][0]}
          to={animationPoints[0][1]}
          title="A tailored process like no other."
          valueProps={[
            {
              illustration: '/images/components/CarQualityAnimationHero/illustration-inspections.svg',
              title: '90-minute inspections',
            },
            {
              illustration: '/images/components/CarQualityAnimationHero/illustration-experts.svg',
              title: '10+ inspection experts',
            },
            {
              illustration: '/images/components/CarQualityAnimationHero/illustration-certified.svg',
              title: 'Verified by the NRMA',
            },
          ]}
          video={{
            ctaLabel: 'Take the Carma tour',
            ctaPoster: `https://img.carma.com.au/ui-assets/CarQualityAnimation/videos/hero.mp4/ik-thumbnail.jpg`,
            ctaVideo: `https://img.carma.com.au/tr:w-400,h-225,ac-none/ui-assets/CarQualityAnimation/videos/hero.mp4`,
            url: 'https://vimeo.com/1086262495',
          }}
        />
        <CarQualityAnimationContentLeftRight
          from={animationPoints[1][0]}
          to={animationPoints[1][1]}
          title="We handpick the highest quality cars in Australia."
          description="Our team is meticulous, and only the best make it to our website."
          alignment="right"
          variant="top"
        />
        <CarQualityAnimationContentLeftRight
          from={animationPoints[2][0]}
          to={animationPoints[2][1]}
          title="Your personal pro test drivers."
          description="We get behind the wheel to road test every aspect of the driver experience."
          video={{
            poster: `https://img.carma.com.au/ui-assets/CarQualityAnimation/videos/test-drivers.mp4/ik-thumbnail.jpg`,
            url: `https://img.carma.com.au/${VIDEO_TRANSFORM}/ui-assets/CarQualityAnimation/videos/test-drivers.mp4`,
          }}
        />
        <CarQualityAnimationContentLeftRight
          from={animationPoints[3][0]}
          to={animationPoints[3][1]}
          title="90+ minutes, 10 experts and a mechanical hoist."
          description="Nothing escapes our forensic inspection process."
          alignment="right"
          theme="light"
          video={{
            poster: `https://img.carma.com.au/ui-assets/CarQualityAnimation/videos/hoist.mp4/ik-thumbnail.jpg`,
            url: `https://img.carma.com.au/${VIDEO_TRANSFORM}/ui-assets/CarQualityAnimation/videos/hoist.mp4`,
          }}
        />
        <CarQualityAnimationContentLeftRight
          from={animationPoints[4][0]}
          to={animationPoints[4][1]}
          title="Reconditioned by our team of specialists."
          description="From testing to fine-tuning, we get it done to our exacting standards."
          video={{
            poster: `https://img.carma.com.au/ui-assets/CarQualityAnimation/videos/reconditioning.mp4/ik-thumbnail.jpg`,
            url: `https://img.carma.com.au/${VIDEO_TRANSFORM}/ui-assets/CarQualityAnimation/videos/reconditioning.mp4`,
          }}
        />
        <CarQualityAnimationContentLeftRight
          from={animationPoints[5][0]}
          to={animationPoints[5][1]}
          title="The finishing touches to showroom-standard."
          description="Deodorising, vacuuming, washing, waxing and buffing. So every car feels like new."
          alignment="right"
          video={{
            poster: `https://img.carma.com.au/ui-assets/CarQualityAnimation/videos/showroom.mp4/ik-thumbnail.jpg`,
            url: `https://img.carma.com.au/${VIDEO_TRANSFORM}/ui-assets/CarQualityAnimation/videos/showroom.mp4`,
          }}
        />
        <ScrollProgress snapPoints={snapPoints} />
      </CarQualityAnimationWrapper>
      {/* <ScrollSnapper snapPoints={snapPoints} /> */}
    </section>
  );
};
